import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Bell, Download, Brain, TrendingUp, TrendingDown,
  DollarSign, Target, Zap, ChevronLeft, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, Flame, BarChart3,
  PieChart, Fish, Eye, Activity, Shield, Clock, Filter,
  AlertTriangle, Layers
} from 'lucide-react';
import './WhaleMovesPage.css';

// =============================================================================
// MOCK DATA — Whale moves: large block trades, unusual options, dark pool
// =============================================================================

const mockWhaleTrades = [
  { id: 1, ticker: 'NVDA', company: 'NVIDIA Corporation', type: 'Block Buy', category: 'block', size: 48200000, shares: 340000, price: 141.76, exchange: 'NYSE', time: '2026-02-10 14:32', sentiment: 'bullish', premium: null, deltaFlow: '+$22.4M', returnSince: 2.1 },
  { id: 2, ticker: 'SPY', company: 'SPDR S&P 500 ETF', type: 'Put Sweep', category: 'options', size: 12800000, shares: null, price: null, exchange: 'CBOE', time: '2026-02-10 13:58', sentiment: 'bearish', premium: '$12.8M', deltaFlow: '-$38.2M', returnSince: -0.4, strike: '$595P', expiry: '2026-03-21' },
  { id: 3, ticker: 'AAPL', company: 'Apple Inc.', type: 'Dark Pool', category: 'darkpool', size: 31500000, shares: 132000, price: 238.64, exchange: 'FINRA ADF', time: '2026-02-10 12:15', sentiment: 'neutral', premium: null, deltaFlow: null, returnSince: 0.8 },
  { id: 4, ticker: 'TSLA', company: 'Tesla Inc.', type: 'Call Sweep', category: 'options', size: 8900000, shares: null, price: null, exchange: 'PHLX', time: '2026-02-10 11:42', sentiment: 'bullish', premium: '$8.9M', deltaFlow: '+$31.5M', returnSince: 3.2, strike: '$420C', expiry: '2026-04-17' },
  { id: 5, ticker: 'META', company: 'Meta Platforms', type: 'Block Buy', category: 'block', size: 24600000, shares: 40200, price: 611.94, exchange: 'NASDAQ', time: '2026-02-10 10:05', sentiment: 'bullish', premium: null, deltaFlow: '+$15.8M', returnSince: 1.4 },
  { id: 6, ticker: 'AMZN', company: 'Amazon.com Inc.', type: 'Dark Pool', category: 'darkpool', size: 18200000, shares: 79800, price: 228.07, exchange: 'FINRA ADF', time: '2026-02-10 09:48', sentiment: 'neutral', premium: null, deltaFlow: null, returnSince: 0.6 },
  { id: 7, ticker: 'AMD', company: 'Advanced Micro Devices', type: 'Put Sweep', category: 'options', size: 5600000, shares: null, price: null, exchange: 'CBOE', time: '2026-02-10 09:35', sentiment: 'bearish', premium: '$5.6M', deltaFlow: '-$18.4M', returnSince: -2.8, strike: '$155P', expiry: '2026-02-21' },
  { id: 8, ticker: 'JPM', company: 'JPMorgan Chase', type: 'Block Buy', category: 'block', size: 15800000, shares: 63500, price: 248.82, exchange: 'NYSE', time: '2026-02-09 15:45', sentiment: 'bullish', premium: null, deltaFlow: '+$9.2M', returnSince: 1.9 },
  { id: 9, ticker: 'QQQ', company: 'Invesco QQQ Trust', type: 'Call Sweep', category: 'options', size: 22100000, shares: null, price: null, exchange: 'ISE', time: '2026-02-09 14:22', sentiment: 'bullish', premium: '$22.1M', deltaFlow: '+$65.3M', returnSince: 1.1, strike: '$530C', expiry: '2026-06-20' },
  { id: 10, ticker: 'MSFT', company: 'Microsoft Corp.', type: 'Dark Pool', category: 'darkpool', size: 42300000, shares: 98600, price: 429.00, exchange: 'FINRA ADF', time: '2026-02-09 13:10', sentiment: 'neutral', premium: null, deltaFlow: null, returnSince: 0.3 },
  { id: 11, ticker: 'PLTR', company: 'Palantir Technologies', type: 'Call Sweep', category: 'options', size: 6400000, shares: null, price: null, exchange: 'CBOE', time: '2026-02-09 11:55', sentiment: 'bullish', premium: '$6.4M', deltaFlow: '+$19.8M', returnSince: 4.8, strike: '$95C', expiry: '2026-03-21' },
  { id: 12, ticker: 'XOM', company: 'Exxon Mobil', type: 'Block Sell', category: 'block', size: 19400000, shares: 178000, price: 108.99, exchange: 'NYSE', time: '2026-02-09 10:30', sentiment: 'bearish', premium: null, deltaFlow: '-$12.1M', returnSince: -1.2 },
  { id: 13, ticker: 'GOOG', company: 'Alphabet Inc.', type: 'Block Buy', category: 'block', size: 28700000, shares: 148000, price: 193.92, exchange: 'NASDAQ', time: '2026-02-08 15:22', sentiment: 'bullish', premium: null, deltaFlow: '+$17.6M', returnSince: 2.3 },
  { id: 14, ticker: 'COIN', company: 'Coinbase Global', type: 'Call Sweep', category: 'options', size: 4200000, shares: null, price: null, exchange: 'PHLX', time: '2026-02-08 14:05', sentiment: 'bullish', premium: '$4.2M', deltaFlow: '+$14.1M', returnSince: 6.7, strike: '$320C', expiry: '2026-03-21' },
  { id: 15, ticker: 'BAC', company: 'Bank of America', type: 'Dark Pool', category: 'darkpool', size: 11200000, shares: 258000, price: 43.41, exchange: 'FINRA ADF', time: '2026-02-08 12:40', sentiment: 'neutral', premium: null, deltaFlow: null, returnSince: 1.0 },
  { id: 16, ticker: 'SOFI', company: 'SoFi Technologies', type: 'Call Sweep', category: 'options', size: 3100000, shares: null, price: null, exchange: 'ISE', time: '2026-02-08 11:18', sentiment: 'bullish', premium: '$3.1M', deltaFlow: '+$10.5M', returnSince: 8.2, strike: '$18C', expiry: '2026-04-17' },
  { id: 17, ticker: 'LMT', company: 'Lockheed Martin', type: 'Block Buy', category: 'block', size: 16500000, shares: 32400, price: 509.26, exchange: 'NYSE', time: '2026-02-07 15:10', sentiment: 'bullish', premium: null, deltaFlow: '+$11.3M', returnSince: 1.5 },
  { id: 18, ticker: 'IWM', company: 'iShares Russell 2000', type: 'Put Sweep', category: 'options', size: 7800000, shares: null, price: null, exchange: 'CBOE', time: '2026-02-07 13:45', sentiment: 'bearish', premium: '$7.8M', deltaFlow: '-$24.6M', returnSince: -1.8, strike: '$210P', expiry: '2026-03-21' },
  { id: 19, ticker: 'UNH', company: 'UnitedHealth Group', type: 'Dark Pool', category: 'darkpool', size: 22800000, shares: 42000, price: 542.86, exchange: 'FINRA ADF', time: '2026-02-07 11:20', sentiment: 'neutral', premium: null, deltaFlow: null, returnSince: 0.9 },
  { id: 20, ticker: 'SMCI', company: 'Super Micro Computer', type: 'Block Sell', category: 'block', size: 9800000, shares: 245000, price: 40.00, exchange: 'NASDAQ', time: '2026-02-07 10:02', sentiment: 'bearish', premium: null, deltaFlow: '-$6.8M', returnSince: -5.4 },
];

const mockTopWhales = [
  { ticker: 'NVDA', value: '$48.2M', type: 'Block Buy', returnSince: '+2.1%' },
  { ticker: 'MSFT', value: '$42.3M', type: 'Dark Pool', returnSince: '+0.3%' },
  { ticker: 'AAPL', value: '$31.5M', type: 'Dark Pool', returnSince: '+0.8%' },
  { ticker: 'GOOG', value: '$28.7M', type: 'Block Buy', returnSince: '+2.3%' },
  { ticker: 'META', value: '$24.6M', type: 'Block Buy', returnSince: '+1.4%' },
];

const mockHotOptions = [
  { ticker: 'QQQ', contract: '$530C Jun 20', premium: '$22.1M', sentiment: 'bullish', volume: '18.2x avg' },
  { ticker: 'SPY', contract: '$595P Mar 21', premium: '$12.8M', sentiment: 'bearish', volume: '12.5x avg' },
  { ticker: 'TSLA', contract: '$420C Apr 17', premium: '$8.9M', sentiment: 'bullish', volume: '9.8x avg' },
  { ticker: 'PLTR', contract: '$95C Mar 21', premium: '$6.4M', sentiment: 'bullish', volume: '14.1x avg' },
  { ticker: 'SOFI', contract: '$18C Apr 17', premium: '$3.1M', sentiment: 'bullish', volume: '22.4x avg' },
];

const mockSectorFlow = [
  { name: 'Technology', pct: 42, color: '#8b5cf6', flow: '+$148M' },
  { name: 'Financials', pct: 18, color: '#3b82f6', flow: '+$42M' },
  { name: 'Healthcare', pct: 14, color: '#10b981', flow: '+$28M' },
  { name: 'Energy', pct: 10, color: '#f59e0b', flow: '-$12M' },
  { name: 'Consumer', pct: 9, color: '#ef4444', flow: '+$18M' },
  { name: 'Defense', pct: 7, color: '#6b7280', flow: '+$16M' },
];

// =============================================================================
// HELPERS
// =============================================================================

const formatCurrency = (val) => {
  if (val >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val}`;
};

const formatShares = (val) => {
  if (!val) return '—';
  if (val >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `${(val / 1e3).toFixed(0)}K`;
  return val.toLocaleString();
};

const formatTime = (t) => {
  const d = new Date(t);
  const month = d.toLocaleString('en-US', { month: 'short' });
  const day = d.getDate();
  const hours = d.getHours();
  const mins = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h = hours % 12 || 12;
  return `${month} ${day}, ${h}:${mins} ${ampm}`;
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

const StatCard = ({ icon: Icon, label, value, sub, subType, color }) => (
  <div className="wm-stat-card" style={{ background: color }}>
    <div className="wm-stat-icon"><Icon size={20} /></div>
    <div className="wm-stat-info">
      <span className="wm-stat-label">{label}</span>
      <span className="wm-stat-value">{value}</span>
      <span className={`wm-stat-sub ${subType}`}>{sub}</span>
    </div>
  </div>
);

const AIInsightCard = ({ insight }) => (
  <div className="wm-ai-insight">
    <div className="wm-ai-insight-header">
      <span className="wm-ai-badge"><Brain size={12} /> AI INSIGHT</span>
      <span>Live flow analysis</span>
    </div>
    <p dangerouslySetInnerHTML={{ __html: insight }} />
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const WhaleMovesPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSentiment, setFilterSentiment] = useState('all');
  const [sortBy, setSortBy] = useState('time');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const filteredTrades = useMemo(() => {
    let result = [...mockWhaleTrades];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.ticker.toLowerCase().includes(q) ||
        t.company.toLowerCase().includes(q) ||
        t.type.toLowerCase().includes(q)
      );
    }

    if (filterCategory === 'block') result = result.filter(t => t.category === 'block');
    if (filterCategory === 'options') result = result.filter(t => t.category === 'options');
    if (filterCategory === 'darkpool') result = result.filter(t => t.category === 'darkpool');

    if (filterSentiment === 'bullish') result = result.filter(t => t.sentiment === 'bullish');
    if (filterSentiment === 'bearish') result = result.filter(t => t.sentiment === 'bearish');

    result.sort((a, b) => {
      const order = sortOrder === 'asc' ? 1 : -1;
      if (sortBy === 'size') return order * (a.size - b.size);
      if (sortBy === 'time') return order * (new Date(a.time) - new Date(b.time));
      if (sortBy === 'return') return order * (a.returnSince - b.returnSince);
      return 0;
    });

    return result;
  }, [searchQuery, filterCategory, filterSentiment, sortBy, sortOrder]);

  const stats = useMemo(() => {
    const blockTrades = mockWhaleTrades.filter(t => t.category === 'block');
    const optionFlows = mockWhaleTrades.filter(t => t.category === 'options');
    const darkPools = mockWhaleTrades.filter(t => t.category === 'darkpool');
    const totalVolume = mockWhaleTrades.reduce((sum, t) => sum + t.size, 0);
    const bullish = mockWhaleTrades.filter(t => t.sentiment === 'bullish').length;
    const bearish = mockWhaleTrades.filter(t => t.sentiment === 'bearish').length;
    return { blockTrades: blockTrades.length, optionFlows: optionFlows.length, darkPools: darkPools.length, totalVolume, bullish, bearish, total: mockWhaleTrades.length };
  }, []);

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const getCategoryClass = (cat) => {
    if (cat === 'block') return 'block';
    if (cat === 'options') return 'options';
    return 'darkpool';
  };

  const getSentimentClass = (s) => {
    if (s === 'bullish') return 'bullish';
    if (s === 'bearish') return 'bearish';
    return 'neutral';
  };

  const aiInsight = `<span class="highlight">Unusual Bullish Flow Detected:</span> Massive call sweeps on <span class="highlight">QQQ</span> totaling $22.1M in premium targeting the $530 strike for June expiry — 18.2x average volume. Meanwhile, <span class="highlight">NVDA</span> saw a $48.2M block buy, the largest single block trade today. Dark pool activity elevated in <span class="highlight">MSFT</span> ($42.3M) and <span class="highlight">AAPL</span> ($31.5M). Overall sentiment skews <span class="highlight">${stats.bullish} bullish vs ${stats.bearish} bearish</span> — institutional money flowing heavily into mega-cap tech.`;

  return (
    <div className="whale-moves-page">
      {/* Header */}
      <div className="wm-page-header">
        <div className="wm-header-content">
          <div className="wm-header-title">
            <div className="wm-header-icon"><Fish size={22} color="#fff" /></div>
            <div>
              <h1>Whale Moves</h1>
              <p>Large block trades, unusual options activity, and dark pool prints</p>
            </div>
          </div>
          <div className="wm-header-actions">
            <button className="wm-header-btn"><Download size={14} /> Export</button>
            <button className="wm-header-btn primary"><Bell size={14} /> Set Alerts</button>
          </div>
        </div>
      </div>

      {/* AI Insight */}
      <AIInsightCard insight={aiInsight} />

      {/* Stats Row */}
      <div className="wm-stats-row">
        <StatCard icon={DollarSign} label="Total Flow (7d)" value={formatCurrency(stats.totalVolume)} sub={`${stats.total} transactions`} subType="neutral" color="linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))" />
        <StatCard icon={Layers} label="Block Trades" value={stats.blockTrades.toString()} sub="Large institutional prints" subType="neutral" color="linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))" />
        <StatCard icon={Activity} label="Options Flow" value={stats.optionFlows.toString()} sub="Unusual sweeps detected" subType="positive" color="linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2))" />
        <StatCard icon={Shield} label="Dark Pool" value={stats.darkPools.toString()} sub="Off-exchange volume" subType="neutral" color="linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(109, 40, 217, 0.2))" />
      </div>

      {/* Content Grid */}
      <div className="wm-content-grid">
        {/* Main Table */}
        <div className="wm-table-panel">
          <div className="wm-panel-header">
            <h2><Fish size={16} /> Whale Activity Feed <span className="wm-record-count">{filteredTrades.length}</span></h2>
          </div>

          {/* Toolbar */}
          <div className="wm-toolbar">
            <div className="wm-search-box">
              <Search size={14} color="rgba(255,255,255,0.3)" />
              <input type="text" placeholder="Search ticker or company..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
            <div className="wm-filter-toggles">
              {['all', 'block', 'options', 'darkpool'].map(f => (
                <button key={f} className={`wm-filter-btn ${filterCategory === f ? 'active' : ''} ${f}`} onClick={() => setFilterCategory(f)}>
                  {f === 'all' ? 'All' : f === 'block' ? 'Blocks' : f === 'options' ? 'Options' : 'Dark Pool'}
                </button>
              ))}
            </div>
            <div className="wm-sentiment-filters">
              {['all', 'bullish', 'bearish'].map(f => (
                <button key={f} className={`wm-sentiment-btn ${filterSentiment === f ? 'active' : ''} ${f}`} onClick={() => setFilterSentiment(f)}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          {filteredTrades.length === 0 ? (
            <div className="wm-empty-state"><Search size={40} /><p>No whale moves match your filters</p></div>
          ) : (
            <div className="wm-table-wrap">
              <table className="wm-table">
                <thead>
                  <tr>
                    <th>Stock</th>
                    <th>Type</th>
                    <th>Sentiment</th>
                    <th className={sortBy === 'size' ? 'sorted' : ''} onClick={() => handleSort('size')}>
                      Size <SortIcon field="size" />
                    </th>
                    <th>Details</th>
                    <th className={sortBy === 'time' ? 'sorted' : ''} onClick={() => handleSort('time')}>
                      Time <SortIcon field="time" />
                    </th>
                    <th className={sortBy === 'return' ? 'sorted' : ''} onClick={() => handleSort('return')}>
                      Return <SortIcon field="return" />
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrades.map(trade => (
                    <tr key={trade.id} onClick={() => navigate(`/stock/${trade.ticker}`)}>
                      <td>
                        <span className="wm-ticker">{trade.ticker}</span>
                        <span className="wm-company">{trade.company}</span>
                      </td>
                      <td>
                        <span className={`wm-type-badge ${getCategoryClass(trade.category)}`}>
                          {trade.type}
                        </span>
                      </td>
                      <td>
                        <span className={`wm-sentiment-badge ${getSentimentClass(trade.sentiment)}`}>
                          {trade.sentiment === 'bullish' ? <TrendingUp size={11} /> : trade.sentiment === 'bearish' ? <TrendingDown size={11} /> : <Activity size={11} />}
                          {trade.sentiment}
                        </span>
                      </td>
                      <td>
                        <span className="wm-size">{formatCurrency(trade.size)}</span>
                        {trade.shares && <span className="wm-shares">{formatShares(trade.shares)} shares</span>}
                      </td>
                      <td>
                        {trade.category === 'options' ? (
                          <div>
                            <span className="wm-contract">{trade.strike} {trade.expiry}</span>
                            <span className="wm-premium">Premium: {trade.premium}</span>
                          </div>
                        ) : trade.category === 'block' ? (
                          <div>
                            <span className="wm-exchange">{trade.exchange}</span>
                            {trade.deltaFlow && <span className="wm-delta">{trade.deltaFlow}</span>}
                          </div>
                        ) : (
                          <div>
                            <span className="wm-exchange">{trade.exchange}</span>
                            <span className="wm-shares">@ ${trade.price?.toFixed(2)}</span>
                          </div>
                        )}
                      </td>
                      <td><span className="wm-time">{formatTime(trade.time)}</span></td>
                      <td>
                        <span className={`wm-return ${trade.returnSince >= 0 ? 'positive' : 'negative'}`}>
                          {trade.returnSince >= 0 ? '+' : ''}{trade.returnSince}%
                        </span>
                      </td>
                      <td>
                        <button className="wm-chart-btn" onClick={(e) => { e.stopPropagation(); navigate(`/chart/${trade.ticker}`); }} title={`Full chart for ${trade.ticker}`}>
                          <BarChart3 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="wm-pagination">
            <span className="wm-pagination-info">Showing {filteredTrades.length} of {mockWhaleTrades.length} whale moves</span>
            <div className="wm-pagination-btns">
              <button className="wm-page-btn" disabled><ChevronLeft size={14} /></button>
              <button className="wm-page-btn active">1</button>
              <button className="wm-page-btn">2</button>
              <button className="wm-page-btn"><ChevronRight size={14} /></button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="wm-sidebar">
          {/* Sentiment Gauge */}
          <div className="wm-sidebar-card">
            <div className="wm-card-header">
              <h3><BarChart3 size={16} color="#667eea" /> Sentiment (7d)</h3>
            </div>
            <div className="wm-ratio-chart">
              <div className="wm-ratio-bar">
                <div className="wm-ratio-bullish" style={{ width: `${(stats.bullish / stats.total) * 100}%` }} />
                <div className="wm-ratio-bearish" style={{ width: `${(stats.bearish / stats.total) * 100}%` }} />
                <div className="wm-ratio-neutral" style={{ width: `${((stats.total - stats.bullish - stats.bearish) / stats.total) * 100}%` }} />
              </div>
              <div className="wm-ratio-labels">
                <span className="wm-ratio-label"><span className="dot bullish" /><span className="label-text">Bullish</span><span className="label-value">{stats.bullish}</span></span>
                <span className="wm-ratio-label"><span className="dot bearish" /><span className="label-text">Bearish</span><span className="label-value">{stats.bearish}</span></span>
                <span className="wm-ratio-label"><span className="dot neutral" /><span className="label-text">Neutral</span><span className="label-value">{stats.total - stats.bullish - stats.bearish}</span></span>
              </div>
            </div>
          </div>

          {/* Largest Whale Trades */}
          <div className="wm-sidebar-card">
            <div className="wm-card-header">
              <h3><Flame size={16} color="#f59e0b" /> Largest Trades (7d)</h3>
            </div>
            <div className="wm-leaderboard">
              {mockTopWhales.map((w, i) => (
                <div className="wm-leader-row" key={i} onClick={() => navigate(`/stock/${w.ticker}`)}>
                  <span className={`wm-leader-rank ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : ''}`}>{i + 1}</span>
                  <div className="wm-leader-info">
                    <span className="wm-leader-ticker">{w.ticker}</span>
                    <span className="wm-leader-meta">{w.type} — {w.value}</span>
                  </div>
                  <span className={`wm-leader-return ${w.returnSince.startsWith('+') ? 'positive' : 'negative'}`}>{w.returnSince}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Hot Options Flow */}
          <div className="wm-sidebar-card">
            <div className="wm-card-header">
              <h3><Zap size={16} color="#f59e0b" /> Hot Options Flow</h3>
            </div>
            <div className="wm-options-list">
              {mockHotOptions.map((opt, i) => (
                <div className="wm-option-item" key={i}>
                  <div className="wm-option-left">
                    <span className="wm-option-ticker">{opt.ticker}</span>
                    <span className="wm-option-contract">{opt.contract}</span>
                  </div>
                  <div className="wm-option-right">
                    <span className="wm-option-premium">{opt.premium}</span>
                    <span className={`wm-option-sentiment ${opt.sentiment}`}>{opt.volume}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sector Flow */}
          <div className="wm-sidebar-card">
            <div className="wm-card-header">
              <h3><PieChart size={16} color="#667eea" /> Sector Flow (7d)</h3>
            </div>
            <div className="wm-sector-list">
              {mockSectorFlow.map((s, i) => (
                <div className="wm-sector-row" key={i}>
                  <span className="wm-sector-name">{s.name}</span>
                  <div className="wm-sector-bar-wrap">
                    <div className="wm-sector-bar-fill" style={{ width: `${s.pct}%`, background: s.color }} />
                  </div>
                  <span className={`wm-sector-flow ${s.flow.startsWith('+') ? 'positive' : 'negative'}`}>{s.flow}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhaleMovesPage;